<template>
    <div v-resize="onResize">
        <v-card
            class="ma-0"
            flat
        >
            <v-card-title class="pa-2">
                <v-btn
                    color="secondary"
                    class="ma-2"
                    @click="navigateBack()"
                >
                    <v-icon large>mdi-chevron-left</v-icon>
                </v-btn>
                <div class="flex-grow-1"></div>
                <div :class="breakpoint === 'xs' ? 'text-h6' : 'text-h5'">{{ groupName }} {{ isControlPlate ? '(Control Plate)' : ''}}</div>

                <v-spacer />

                <div v-if="backupFileName" class="mr-2 d-flex flex-wrap">
                    <v-icon large color="green">mdi-book</v-icon>
                    <span style="line-height : 15px" class="py-1 text-caption">Backup <br /> Enabled</span>
                </div>
                <div v-else-if="isBackupCapabilityAvailable" class="d-flex flex-wrap mr-2">
                    <v-icon large color="red">mdi-book-cancel</v-icon>
                    <span style="line-height : 15px" class="pl-1 py-1 text-caption">Backup<br />Disabled</span>
                </div>
            </v-card-title>
            <v-card-text>
                <v-data-table
                    :height="tableHeight"
                    fixed-header
                    mobile-breakpoint="300"
                    disable-sort
                    disable-pagination
                    dense
                    :headers="tblHeaders"
                    :items="expandedWells"
                    item-key="wingband"
                    :search="searchTable"
                    :group-by="this.showPlates? 'plateNumber' : null"
                    hide-default-footer
                >
                    <template v-slot:top>
                        <!-- Row 1 -->
                        <v-row dense>
                            <v-col cols="1"></v-col>
                            <v-col cols="2" class="py-0">
                                <v-select
                                    v-model="selectedLocation"
                                    :items="locationList"
                                    item-text="name"
                                    item-value="id"
                                    label="Location"
                                    return-object
                                    clearable
                                >
                                    <template v-slot:selection="data">
                                        {{ data.item.house }}.{{ data.item.location }}
                                    </template>

                                    <template v-slot:item="data">
                                        <v-list-item-content>
                                        <v-list-item-title>{{ data.item.batchNum }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                        House: {{ data.item.house }}<br>
                                        Location: {{ data.item.location }}<br />
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="2">
                                <v-text-field class="pl-1 pt-2 pr-2"
                                    v-model="searchTable"
                                    label="Search"
                                    clearable
                                />
                            </v-col>
                            <v-col cols="2">
                                <v-switch
                                    v-if="!isControlPlate"
                                    v-model="showPlates"
                                    inset
                                    label="Show Plates/Wells"
                                />
                            </v-col>
                        </v-row>

                        <!-- Row 2 -->
                        <v-row dense>
                            <v-col cols="1">
                                <v-btn
                                :class="`white--text ml-1 mr-2 ${$vuetify.breakpoint.smAndDown ? 'mt-2' : ''}`"
                                    color="primary"
                                    fab
                                    :small="$vuetify.breakpoint.smAndDown"
                                    :disabled="isAddDisabled"
                                    @click="wingbandEntered()"
                                >
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                            </v-col>
                            <v-col cols="2">
                                <v-text-field
                                    v-model="wingband"
                                    ref="wingbandRef"
                                    label="Wingband"
                                    clearable
                                    @keypress="maskAlphaNumeric($event)"
                                    @keydown.enter="wingbandEntered()"
                                    @keyup="wingband = maskWingband(wingband)"
                                    :rules="[v => isWingbandValidRule(v), v => isWingbandExistingRule(v)]"
                                />
                            </v-col>
                            <v-col cols="2">
                                <v-select
                                   :disabled="!showPlates"
                                    v-model="currentBatchSettings.plateDescr"
                                    label="Plate Number"
                                    :items="plateDropdownValues"
                                    @change="currentPlateChanged()"
                                />
                            </v-col>
                            <v-col cols="2">
                                <v-select class="mx-1"
                                    v-model="currentBatchSettings.wellNumber"
                                    label="Well Number"
                                    :items="wellNumberList"
                                />
                            </v-col>
                            <v-col cols="4" class="text-right">
                                <v-btn class="mx-1"
                                    color="warning"
                                    fab
                                    :small="$vuetify.breakpoint.smAndDown"
                                    :disabled="isReplaceDisabled"
                                    @click="confirmReplacement()"
                                >
                                    <v-icon>mdi-file-replace-outline</v-icon>
                                </v-btn>
                                <v-btn color="secondary"
                                    class="mx-1"
                                    fab 
                                    :small="$vuetify.breakpoint.smAndDown"
                                    :disabled="isDeleteDisabled"
                                    @click="confirmDeleteWell()"
                                >
                                    <v-icon>mdi-trash-can-outline</v-icon>
                                </v-btn>
                                <v-btn color="error"
                                    class="mx-1"
                                    fab
                                    :small="$vuetify.breakpoint.smAndDown"
                                    :disabled="isPlateDeleteDisabled"
                                    @click="confirmDeletePlate()"
                                >
                                    <v-icon>mdi-grid-off</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                        <div style="display: flex; justify-content: center;">
                            <table style="width: 100%; margin: auto;">
                                <tr>
                                    <td v-for="location in locationTotals" v-bind:key="location.location" style="text-align: center;">
                                        {{ location.location }} ({{ location.totalCollected }} of {{ location.totalNeeded }})
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </template>

                    <template v-slot:item="{ item }">
                        <tr @click="setCurrentWell(item)">
                            <td :class="item.wingband ? '' : 'grey--text'">{{ item.tubeNumber }}</td>
                            <td v-if="showPlates" :class="item.wingband ? '' : 'grey--text'">{{ item.wellNumber }}</td>
                            <td :class="item.wingband ? '' : 'grey--text'">{{ item.wingband }}</td>
                            <td :class="item.wingband ? '' : 'grey--text'">{{ item.sampleId }}</td>
                            <td :class="item.wingband ? '' : 'grey--text'">{{ item.line }}</td>
                            <td :class="item.wingband ? '' : 'grey--text'">{{ item.age }}</td>
                            <td :class="item.wingband ? '' : 'grey--text'">{{ item.location }}</td>
                            <td :class="item.wingband ? '' : 'grey--text'">{{ item.flock }}</td>
                            <td :class="item.wingband ? '' : 'grey--text'">{{ formatDate(item.date) }}</td>
                        </tr>
                    </template>

                    <template v-slot:[`group.header`]="{group, items, isOpen, toggle}">
                        <th colspan="9">
                            <div class="d-flex">
                                <v-icon @click="toggle" :ref="group" :data-open="isOpen">
                                    {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
                                </v-icon>
                                <div class="pl-1 pt-1 pr-12">Plate {{ items[0].plateNumber }} 
                                    <span class="ml-3">
                                        ({{ getWellCountForPlate(items[0].plateNumber) }} of {{ wellNumberList.length }})
                                    </span>
                                </div>
                            </div>
                        </th>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="showReplaceDialog" persistent max-width="350">
            <v-card color="cardbackground">
                <v-card-title class="pl-5 text-h5">Replace</v-card-title>
                <v-card-subtitle class="pt-1 pl-5">
                    Wingband: <b>{{ wellToReplace.wingband}}</b><br />
                    Plate: <b>{{ wellToReplace.plateNumber }}</b>,
                    Well Number: <b>{{ wellToReplace.wellNumber }}</b>
                </v-card-subtitle>
                <v-text-field
                    class="mx-5"
                    ref="replaceWingbandRef"
                    v-model="newWingband"
                    label="Wingband"
                    clearable
                    @keypress="maskAlphaNumeric($event)"
                    @keydown.enter="replaceWingband(newWingband)"
                    @keyup="newWingband = maskWingband(newWingband)"
                    :rules="[v => isWingbandValidRule(v), v => isWingbandExistingRule(v)]"
                />
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary" 
                        :disabled="isReplaceDialogButtonDisabled"
                        class="white--text"
                        @click="replaceWingband()"
                    >
                        Replace
                    </v-btn>
                    <v-btn color="secondary" @click="cancelWingbandReplacement()">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showDeletePlateDialog" persistent max-width="350">
            <v-card color="cardbackground">
                <v-card-title class="text-h5">Confirm Delete {{ currentBatchSettings.plateDescr }}</v-card-title>
                <v-card-text>
                    <div>There are currently <b>{{ getWellCountForPlate(currentPlateNum) }}</b> 
                        filled wells for this plate.
                    </div>
                    <div>Is it okay to Delete <b>{{ currentBatchSettings.plateDescr }}</b>?</div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" class="white--text" @click="deletePlate()">Yes</v-btn>
                    <v-btn color="secondary" @click="showDeletePlateDialog = false">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showDeleteWellDialog" persistent max-width="350">
            <v-card color="cardbackground">
                <v-card-title class="text-h5">Confirm Delete Well</v-card-title>
                <v-card-text>
                    <div v-if="showPlates">
                        Is it okay to Delete <b>{{ wingband }}</b>
                        for <b>{{ currentBatchSettings.plateDescr }} {{ currentBatchSettings.wellNumber }}</b>?
                    </div>
                    <div v-else>
                        Is it okay to Delete <b>{{ wingband }}</b>
                        for Tube <b>{{ getTubeNumberFromWellNumber(this.currentPlateNum, currentBatchSettings.wellNumber) }}</b>?
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" class="white--text" @click="deleteWell()">Yes</v-btn>
                    <v-btn color="secondary" @click="showDeleteWellDialog = false">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import { format } from 'date-fns';
import OfflineStorage from '../offlineStorage';
import { mapState } from 'vuex';
import fileSysApi from '@/api/fileSysApi';

const WELL_NUMBERS_CONTROL = ['A7', 'A8', 'A9', 'A10', 'A11', 'A12', 
    'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'B10', 'B11', 'B12',
    'C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9', 'C10', 'C11', 'C12',
    'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'D9', 'D10', 'D11', 'D12',
    'E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7', 'E8', 'E9', 'E10', 'E11', 'E12',
    'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
    'G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10', 'G11', 'G12',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'H7', 'H8', 'H9', 'H10', 'H11', 'H12'
];
Object.seal(WELL_NUMBERS_CONTROL);

const WELL_NUMBERS_FULL = [
    'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'A11', 'A12', 
    'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'B10', 'B11', 'B12',
    'C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9', 'C10', 'C11', 'C12',
    'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'D9', 'D10', 'D11', 'D12',
    'E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7', 'E8', 'E9', 'E10', 'E11', 'E12',
    'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
    'G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10', 'G11', 'G12',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'H7', 'H8', 'H9', 'H10', 'H11', 'H12'
];
Object.seal(WELL_NUMBERS_FULL);

export default {
    data() {
        return {
            backupFileName: null,
            batch: {},
            batches: [],
            healthBleedGroups: [],
            currentBatchSettings: {
                age: null,
                airSpace: null,
                flock: null,
                line: null,
                plateDescr: 'Plate1',
                wellNumber: null
            },
            plateDropdownValues: ['Plate1', 'Plate2', 'Plate3', 'Plate4', 'Plate5', 'Plate6', 'Plate7'],
            rules: null,
            searchTable: null,
            showPlates: true,

            selectedLocation: null,

            showDeleteWellDialog: false,
            showDeletePlateDialog: false,
            showReplaceDialog: false,
            newWingband: null,
            wingband: null,
            wells: [],
            wellToReplace: {},
            windowSize: { x: 0, y: 0 }
        }
    },

    async mounted() {
        this.onResize();
        this.closeAllPlateGroups();
        this.$refs.wingbandRef.focus();

        this.currentBatchSettings.wellNumber = this.isControlPlate ? WELL_NUMBERS_CONTROL[0] : WELL_NUMBERS_FULL[0];

        try {
            var permission = await fileSysApi.getDirPermissions(this.settings.healthBackupDir);
            if (permission == 'granted') {
                this.backupFileName = await fileSysApi.getNewFileName(this.settings.healthBackupDir, this.groupName);
            }
        }
        catch(error) {
            this.$root.showNoticeError(`Unable to get Backup File Handle: ${error.message}`);
        }
    },

    async created() {
        this.rules = { ...this.$root.validationRules };
        try {
            this.healthBleedGroups = await OfflineStorage.getItem('healthBleedGroups');

            if (this.healthBleedGroups == null) {
                this.healthBleedGroups = [];
            }

            var group = this.healthBleedGroups.find(x => x.groupName === this.groupName);
            if (group.currentBatchSettings == null) {
                group.currentBatchSettings = this.currentBatchSettings;
            }
            else {
                this.currentBatchSettings = group.currentBatchSettings;
            }

            this.batches = group.batches;
            this.batch = group.batches[0];

            this.wells = group.wells;
            if (this.wells.length > 0) {
                let firstOpenWell = this.expandedWells.find(w => w.wingband == null);
                if (firstOpenWell) {
                    this.currentBatchSettings.plateDescr = 'Plate' + firstOpenWell.plateNumber;
                    this.currentBatchSettings.wellNumber = firstOpenWell.wellNumber;
                }
                else {
                    this.currentBatchSettings.plateDescr = 'Plate7';
                }
            }

            let lastFilledWell = [...this.expandedWells].reverse().find(w => w.wingband != null);
            if (lastFilledWell != null) {
                this.currentBatchSettings.age = lastFilledWell.age;
                this.currentBatchSettings.location = lastFilledWell.location;
                this.currentBatchSettings.flock = lastFilledWell.flock;
                this.currentBatchSettings.line = lastFilledWell.line;
            }

            this.currentPlateChanged();

            this.$nextTick(this.scrollIntoView(this.currentBatchSettings.wellNumber));
        } catch(error) {
            this.$root.showNoticeError(error.message);
        }
    },

    computed: {
        ...mapState(['settings']),
        tblHeaders() {
            let headers = [
                { align: 'start', text: 'Wingband', value: 'wingband' },
                // { align: 'start', text: 'Batch ID', value: 'batchNum' },
                { align: 'start', text: 'Sample ID', value: 'sampleId' },
                { align: 'start', text: 'Lines', value: 'line' },
                { align: 'start', text: 'Weeks', value: 'age' },
                { align: 'start', text: 'Location', value: 'location' },
                { align: 'start', text: 'Flock', value: 'flock' },
                { align: 'start', text: 'Date', value: 'date' },
            ];

            if (this.showPlates) {
                headers = [
                    { align: 'start', text: 'Plate #', value: 'plateNumber' },
                    { align: 'start', text: 'Tube #', value: 'tubeNumber' },
                    { align: 'start', text: 'Well #', value: 'wellNumber' },
                    ...headers,
                ];
            } else {
                headers = [
                    { align: 'start', text: 'Tube #', value: 'tubeNumber' },
                    ...headers,
                ];
            }

            return headers;
        },
        locationList() {
            const distinctLocations = [];
            const batchNumbers = this.batches.map(batch => batch.batchNum);
            var batchDetails = this.settings.farmBatchInfo.filter(x => batchNumbers.includes(x.batchNum));
            if (batchDetails == null) return distinctLocations;

            batchDetails.forEach(batchDetail => 
                batchDetail.samples.forEach(sample => {
                    const exists = distinctLocations.some(s => s.house === sample.house && s.location === sample.location);
                    if (!exists) {
                        distinctLocations.push({ house: sample.house, location: sample.location });
                    }
            }));
            return distinctLocations;
        },
        qapSamples() {
            // Get batch numbers from the batches array
            const batchNumbers = this.batches.map(batch => batch.batchNum);

            // Filter batch details based on batch numbers
            const batchDetails= this.settings.farmBatchInfo.filter(x => batchNumbers.includes(x.batchNum));

            // Create a single array to hold all samples
            const samplesByBatch = batchDetails.flatMap(batch => {
                return batch.samples.map(sample => {
                    return {
                        ...sample,
                        batchNum: batch.batchNum,
                        locationWithHouse: `${sample.house}.${sample.location}`
                    };
                });
            });

            // Remove commas from each sample field
            samplesByBatch.forEach(sample => {
                for (let key in sample) {
                    if (typeof sample[key] === 'string') {
                        sample[key] = sample[key].replace(/,/g, ' ');
                    }
                }
            });

            return samplesByBatch;
        },
        locationTotals() {
            const countData = this.expandedWells.reduce((loc, well) => {
                if (!well.location) return loc; // Skip undefined locations
                if (!loc[well.location]) {
                    loc[well.location] = { totalNeeded: 0, totalCollected: 0 };
                }
                loc[well.location].totalNeeded += 1; // Increment totalNeeded for each well
                if (well.wingband) {
                    loc[well.location].totalCollected += 1; // Increment totalCollected only if wingband exists
                }
                return loc;
            }, {});

            // Convert to array of objects with 'total' key
            const locationTotals = Object.keys(countData).map(location => ({
                location,
                totalNeeded: countData[location].totalNeeded,
                totalCollected: countData[location].totalCollected
            }));
            return locationTotals;
        },
        breakpoint() {
            return this.$vuetify.breakpoint.name;
        },
        currentPlateNum() {
            const num = parseInt(this.currentBatchSettings.plateDescr.replace(/\D/g,''));
            if (isNaN(num)) return -1;
            return num;
        },
        expandedWells() {
            var allWells = [];
            var sampleIndex = 0;
            for (var i=1; i <= this.plateDropdownValues.length; i++) {
                for (var j = 0; j<this.wellNumberList.length; j++) {
                    var well = this.wells.find(x=>x.plateNumber == i && x.wellNumber == this.wellNumberList[j]);
                    if (well == null) {
                        well = {
                            wingband: null,
                            plateNumber: i,
                            tubeNumber: sampleIndex + 1,
                            wellNumber: this.wellNumberList[j],
                            // batchId: this.qapSamples[sampleIndex]?.batchNum,
                            sampleId: this.qapSamples[sampleIndex]?.sampleId,
                            age: this.qapSamples[sampleIndex]?.age,
                            location: this.qapSamples[sampleIndex]?.locationWithHouse,
                            flock: this.qapSamples[sampleIndex]?.flocks,
                            line: this.qapSamples[sampleIndex]?.lines,
                            date: null
                        };
                    }
                    sampleIndex++;

                    allWells.push(well); 
                }
            }
            return allWells;
        },
        groupName() {
            return this.$route.query.groupName;
        },
        plateType() {
            return this.$route.query.type;
        },
        isControlPlate() {
            return this.plateType?.toUpperCase().includes('CONTROL');
        },
        isAddDisabled() {
            if (!this.wingband) return true;

            if (!this.selectedLocation) return true;

            var msg = this.isWingbandValidRule(this.wingband);
            if (msg != true) return true;

            const well = this.wells.find(w => w.plateNumber === this.currentPlateNum 
                                            && w.wellNumber === this.currentBatchSettings.wellNumber 
                                            && w.wingband != null);
            if (well) {
                return true;
            }
            return false;
        },
        isBackupCapabilityAvailable() {
            return fileSysApi.isAvailable;
        },
        isDeleteDisabled() {
            return this.isReplaceDisabled;
        },
        isReplaceDisabled() {
            if (this.isWingbandValidRule(this.wingband) != true) return true;

            const well = this.wells.find(w => w.wellNumber === this.currentBatchSettings.wellNumber
                                            && w.plateNumber === this.currentPlateNum
                                            && w.wingband === this.wingband);
            return !well;
        },
        isReplaceDialogButtonDisabled() {
            if (this.newWingband == null) return true;
            if (this.isWingbandValidRule(this.newWingband) != true) return true;
            if (this.isWingbandExistingRule(this.newWingband)  != true) return true;
            const well = this.wells.find(w => w.wellNumber === this.currentBatchSettings.wellNumber
                                            && w.plateNumber === this.currentPlateNum
                                            && w.wingband === this.wingband);
            return well === undefined;
        },
        isPlateDeleteDisabled() {
            if (this.currentBatchSettings.plateDescr == null) return true;
            if (this.showPlates == false) return true;
            const well = this.wells.find(w => w.plateNumber === this.currentPlateNum);
            return !well;
        },
        tableHeight() {
            let height = this.windowSize.y - 360;

            if (this.$vuetify.breakpoint.name == 'xs' || this.$vuetify.breakpoint.name === 'sm') {
                height += 4;  // v-app-bar shorter for xs and sm
                height += 28; // xs and sm have smaller buttons.
            }

            if (height < 280) height = 280;

            return height;
        },
        wellNumberList() {
            if (this.plateType?.toUpperCase().includes("FULL"))
                return WELL_NUMBERS_FULL;
            else 
                return WELL_NUMBERS_CONTROL;
        }
    },

    methods: {
        cancelWingbandReplacement() {
            this.newWingband = null;
            this.showReplaceDialog = false
        },
        closeAllPlateGroups() {
            Object.keys(this.$refs).forEach(k => {
                if (this.$refs[k] && this.$refs[k].$attrs['data-open']) {
                    this.$refs[k].$el.click();
                }
            });
        },
        confirmDeletePlate() {
            this.showDeletePlateDialog = true;
        },
        confirmDeleteWell() {
            this.showDeleteWellDialog = true;
        },
        confirmReplacement() {
            let ndx = this.wells.findIndex(w => w.plateNumber === this.currentPlateNum
                                            && w.wellNumber == this.currentBatchSettings.wellNumber
                                            && w.wingband === this.wingband);
            this.wellToReplace = this.wells[ndx];
            this.showReplaceDialog = true;
        },
        currentPlateChanged() {
            let currPlateNum = this.plateDropdownValues.indexOf(this.currentBatchSettings.plateDescr) + 1;
            this.closeAllPlateGroups();
            this.$nextTick(() => this.openPlateGroup(currPlateNum));
        },
        deletePlate() {
            let ndx = 0;
            while (ndx > -1) {
                ndx = this.wells.findIndex(w => w.plateNumber === this.currentPlateNum); 
                if (ndx > -1) {
                    this.wells.splice(ndx,1);
                }
            }
            this.saveData();
            this.showDeletePlateDialog = false;
        },
        deleteWell() {
            const wellIndex = this.wells.findIndex(w => `Plate${w.plateNumber}` === this.currentBatchSettings.plateDescr 
                                                    && w.wellNumber === this.currentBatchSettings.wellNumber
                                                    && w.wingband === this.wingband);
            if (wellIndex === -1) {
                throw new Error(`Internal Error: Unable to find well Index for ${this.wingband}`);
            }
            this.wells.splice(wellIndex,1);

            this.saveData();
            this.showDeleteWellDialog = false;
        },
        formatDate(dt) {
            if (dt == null) return dt;
            if (this.breakpoint === 'xs') {
                return dt.split(' ')[0];
            }
            return dt;
        },
        getTubeNumberFromWellNumber(plateNumber, wellNumber) {
            let well = this.expandedWells.find(w => w.wellNumber == wellNumber && w.plateNumber == plateNumber );
            return well.tubeNumber;
        },
        getWellNumberfromTubeNumber(tubeNumber) {
            let wellNumber = this.wellNumberList[tubeNumber-1];
            return wellNumber;
        },
        getWellCountForPlate(plateNum) {
            const numWells = this.wells.filter(w=>w.plateNumber === plateNum).length;
            return numWells;
        },
        isWingbandValidRule(v) {
            if (!v) return true;

            if (/^[A-Z]{3}\d{4}$/.test(v) === false && /^\d{5}(?:\d{1})?$/.test(v) === false) {
                return 'Invalid Wingband';
            }
            else return true;
        },
        isWingbandExistingRule(v) {
            if (!v) return true;

            let wingbandIndex = this.wells.findIndex(w=>w.wingband === v);
            if (wingbandIndex != -1) {
                return 'Duplicate Wingband';
            }
            return true;
        },
        makeSound() {
            if (this.settings.isSuccessSoundEnabled) {
                const audio = new Audio('sound/success-chime.mp3');
                audio.play();
            }
        },
        maskAlphaNumeric(e) {
            let char = String.fromCharCode(e.keyCode);
            if(/^[A-Za-z0-9]+$/.test(char)) 
                return char.toUpperCase();
            else
                e.preventDefault(); // If not match, don't add to input text
        },
        maskWingband(wingband) {
            if (wingband == null) {
                return;
            }
            let char = wingband.charAt(0);
            let str = char;
            let i = 0;

            // Check for AAA#### format
            if (/^[A-Za-z]+$/.test(char)) { //isAlpha
                for (i = 1; i < wingband.length && i<3; i++) {
                    char = wingband.charAt(i);
                    if (/^[A-Za-z]+$/.test(char)) str += char;
                }

                if (str.length < 3) return str.toUpperCase();

                for (i = 3; i < wingband.length && i<7; i++) {
                    char = wingband.charAt(i);
                    if (/^[0-9]+$/.test(char)) str += char;
                }
            }
            else { /// Must be ###### format
                for (i = 1; i < wingband.length && i<6; i++) {
                    char = wingband.charAt(i);
                    if (/^[0-9]+$/.test(char)) str += char;
                }
            }

            return str.toUpperCase();
        },
        navigateBack() {
            this.$router.go(-1);
        },
        onResize() {
            this.windowSize = { x: window.innerWidth, y: window.innerHeight }
        },
        openPlateGroup(plateNum) {
            let plateCnt = 0;
            Object.keys(this.$refs).forEach(k => {
                plateCnt++;
                if (this.$refs[k] && !this.$refs[k].$attrs['data-open']) {
                    if (plateCnt === plateNum) {
                        this.$refs[k].$el.click();
                    }
                }
            });
        },
        replaceWingband() {
            if (this.isWingbandValidRule(this.newWingband) != true) return;
            if (this.isWingbandExistingRule(this.newWingband)  != true) return;
            this.wellToReplace.wingband = this.newWingband;
            this.showReplaceDialog = false;

            this.wingband = null;
            this.newWingband = null;

            this.saveData();
        },
        async saveData() {
            await OfflineStorage.setItem('healthBleedGroups', this.healthBleedGroups); //Indexed DB
            try {
                if (this.backupFileName) { //Local File
                     var group = this.healthBleedGroups.find(x => x.groupName === this.groupName);
                    var header = "FarmCode,LineCode,Hatch,AgeNum,Location,Flock,Tube,Plate,Well,Wingband,Date";
                    var rows = this.wells.map(w => `${group.farm},${w.line},${group.hatch},${w.age},${w.location},${w.flock},${w.tubeNumber},${w.plateNumber},${w.wellNumber},${w.wingband},${w.date}`);
                    await fileSysApi.saveToFile(this.settings.healthBackupDir, this.backupFileName, header, rows);
                }
            }
            catch (error) {
                throw new Error (`Backup Failed. ${error.message}`)
            }
        },
        setCurrentWell(item) {
            this.wingband = item.wingband;
            this.currentBatchSettings.wellNumber = item.wellNumber;
            this.currentBatchSettings.plateDescr = 'Plate' + item.plateNumber;

            this.$refs.wingbandRef.focus();
        },
        sortWells() {
            this.wells.sort((a,b) => {
                if (a.plateNumber < b.plateNumber) return -1
                if (a.plateNumber > b.plateNumber) return 1;

                const aIndex = this.wellNumberList.indexOf(a.wellNumber);
                const bIndex = this.wellNumberList.indexOf(b.wellNumber);
                if (aIndex < bIndex) return -1;
                return 1;
            });
        },
        wingbandEntered() {
            try {
                if (!this.wingband) return;

                // Validate addition
                var msg = this.isWingbandValidRule(this.wingband);
                if (msg != true) throw new Error(msg + '. Valid formats are AAA1234 or 12345 or 123456.');

                if (!this.selectedLocation) msg = "Please select a location.";
                if (msg != true) throw new Error(msg);

                // Check if wingband already collected.
                let ndx = this.wells.findIndex(w=>w.wingband === this.wingband);
                if (ndx != -1) {
                    let well = this.wells[ndx];
                    const position = `well: ${well.wellNumber}`;
                    this.$root.showNoticeError(`Wingband ${well.wingband} already exists (plate: ${well.plateNumber} ${position}})`); 
                    return;
                }

                // Check if well already used.
                const well = this.wells.find(w => w.plateNumber === this.currentPlateNum
                                && w.wellNumber === this.currentBatchSettings.wellNumber);
                if (well) {
                    const position = `well: ${this.currentBatchSettings.wellNumber}`;
                    this.$root.showNoticeError(`Position (plate: ${this.currentPlateNum}, ${position}) already filled.`); 
                    return;
                }

                let tubeNumber = this.getTubeNumberFromWellNumber(this.currentPlateNum, this.currentBatchSettings.wellNumber);

                // Check if sample is for correct location.
                if (this.qapSamples[tubeNumber-1]?.location != this.selectedLocation.location) {
                    const selectedLocation = `${this.selectedLocation.location}`;
                    const selectedHouse = `${this.selectedLocation.house}`;
                    const sampleLocationWithHouse = `${this.qapSamples[tubeNumber-1]?.locationWithHouse}`;
                    this.$root.showNoticeError(`Selected Location (${selectedHouse}.${selectedLocation}) ` +
                        `and Sample Location (${sampleLocationWithHouse}) ` +
                        `do not match for Tube ${tubeNumber}.`); 
                    return;
                }
                // Add sample.
                this.wells.push({
                    wingband: this.wingband, 
                    wellNumber: this.currentBatchSettings.wellNumber,
                    tubeNumber: this.getTubeNumberFromWellNumber(this.currentPlateNum, this.currentBatchSettings.wellNumber),
                    plateNumber: this.currentPlateNum,
                    age: this.qapSamples[tubeNumber-1]?.age,
                    location: this.qapSamples[tubeNumber-1]?.locationWithHouse,
                    flock: this.qapSamples[tubeNumber-1]?.flocks,
                    line: this.qapSamples[tubeNumber-1]?.lines,
                    sampleId: this.qapSamples[tubeNumber-1]?.sampleId,
                    date: format(new Date(),  'yyyy-MM-dd hh:mm:ss aa')
                });

                this.sortWells();
                this.saveData();

                let currNdx = this.expandedWells.findIndex(w=>w.wingband === this.wingband);
                let nextWell = this.expandedWells.find((w,i) =>w.wingband == null && i > currNdx);
                if (nextWell) {
                    this.currentBatchSettings.wellNumber = nextWell.wellNumber;
                    this.currentBatchSettings.plateDescr = 'Plate' + nextWell.plateNumber;
                    this.currentPlateChanged(nextWell.plateNumber);
                }

                this.$nextTick(this.scrollIntoView(this.currentBatchSettings.wellNumber));
                this.wingband = null;
                this.makeSound();
            } 
            catch(error) {
                this.$root.showNoticeError(error.message);
            }
        },
        scrollIntoView(tableTxt) {
            const tdTags = document.querySelectorAll("td");
            for (const a of tdTags) {
                if (a.textContent == tableTxt) {
                    a.scrollIntoViewIfNeeded(true);
                }
            }
        }
    }
}
</script>